import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';

import { cx } from '@/styles/muiTheme';

import { useDispatchWebhooksQuery } from '../hooks/useDispatchWebhooksQuery';
import { useCreateDispatchWebhookMutation } from '../hooks/useCreateDispatchWebhookMutation';
import { useDeleteDispatchWebhookMutation } from '../hooks/useDeleteDispatchWebhookMutation';
import { useDispatchEmailConfigQuery } from '../hooks/useDispatchEmailConfigQuery';
import { useCreateDispatchEmailConfigMutation } from '../hooks/useCreateDispatchEmailConfigMutation';
import { useDeleteDispatchEmailConfigMutation } from '../hooks/useDeleteDispatchEmailConfigMutation';
import { useDispatchS3DestinationsQuery } from '../hooks/useDispatchS3DestinationsQuery';
import { useCreateDispatchS3DestinationMutation } from '../hooks/useCreateDispatchS3DestinationMutation';
import { useDeleteDispatchS3DestinationMutation } from '../hooks/useDeleteDispatchS3DestinationMutation';
import type { CreateDispatchWebhookData } from '../hooks/useCreateDispatchWebhookMutation';
import type { CreateS3DestinationData } from '../hooks/useCreateDispatchS3DestinationMutation';
import type { S3DestinationAuthMode } from '../hooks/useDispatchS3DestinationsQuery';

// Section subtitles use the mid-grey step of the theme ramp — muted against the
// white titles, but still comfortably readable.
const SUBTITLE_SX = { color: cx.text.tertiary , opacity : 0.9} as const;

const WEBHOOK_TYPE_LABELS: Record<string, string> = {
  slack: 'Slack',
  teams: 'Teams',
  discord: 'Discord',
  generic: 'Generic',
};

const SCOPE_LABELS: Record<string, string> = {
  org: 'Org',
  department: 'Department',
  user: 'User',
};

const SCOPE_COLORS: Record<string, 'primary' | 'secondary' | 'default'> = {
  org: 'primary',
  department: 'secondary',
  user: 'default',
};

function webhookScope(wh: { orgId: number | null; departmentId: number | null; userId: number | null }) {
  if (wh.userId) return 'user';
  if (wh.departmentId) return 'department';
  return 'org';
}

// ─── Webhooks Section ────────────────────────────────────────────────────────

const WebhooksSection: React.FC = () => {
  const { data: webhooks = [], isLoading } = useDispatchWebhooksQuery();
  const createMutation = useCreateDispatchWebhookMutation();
  const deleteMutation = useDeleteDispatchWebhookMutation();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<Partial<CreateDispatchWebhookData>>({
    type: 'slack',
    scope: 'org',
  });

  const handleCreate = async () => {
    if (!form.url || !form.type || !form.scope) return;
    await createMutation.mutateAsync(form as CreateDispatchWebhookData);
    setDialogOpen(false);
    setForm({ type: 'slack', scope: 'org' });
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" mb={2} sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6">Webhooks</Typography>
            <Typography variant="body2" sx={SUBTITLE_SX}>
              HTTP POST on every clip export. Scope to org, department, or your account.
            </Typography>
          </Box>
          <Button startIcon={<AddIcon />} variant="contained" size="small" onClick={() => setDialogOpen(true)}>
            Add
          </Button>
        </Stack>

        {isLoading && <Typography color="text.secondary">Loading…</Typography>}

        {!isLoading && webhooks.length === 0 && (
          <Alert severity="info" sx={{ mt: 1 }}>No webhooks configured.</Alert>
        )}

        <Stack spacing={1} sx={{ mt: 1 }}>
          {webhooks.map((wh) => {
            const scope = webhookScope(wh);
            return (
              <Stack
                key={wh.id}
                direction="row"
                sx={{ px: 1.5, py: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, alignItems: 'center', justifyContent: 'space-between' }}
              >
                <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                  <Chip label={WEBHOOK_TYPE_LABELS[wh.type] ?? wh.type} size="small" />
                  <Chip label={SCOPE_LABELS[scope]} size="small" color={SCOPE_COLORS[scope]} variant="outlined" />
                  <Box>
                    {wh.label && <Typography variant="body2" fontWeight={500}>{wh.label}</Typography>}
                    <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all', fontFamily: 'monospace' }}>
                      {wh.url}
                    </Typography>
                  </Box>
                </Stack>
                <Tooltip title="Remove">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => deleteMutation.mutate(wh.id)}
                    disabled={deleteMutation.isPending}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            );
          })}
        </Stack>
      </CardContent>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Webhook</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              fullWidth
              label="Type"
              value={form.type ?? 'slack'}
              onChange={(e) => setForm((p) => ({ ...p, type: e.target.value as any }))}
            >
              {Object.entries(WEBHOOK_TYPE_LABELS).map(([v, l]) => (
                <MenuItem key={v} value={v}>{l}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              fullWidth
              label="Scope"
              value={form.scope ?? 'org'}
              onChange={(e) => setForm((p) => ({ ...p, scope: e.target.value as any, departmentId: undefined }))}
              helperText="Who this webhook fires for"
            >
              <MenuItem value="org">Org — all clips in the org</MenuItem>
              <MenuItem value="department">Department — clips in a specific department</MenuItem>
              <MenuItem value="user">User — only my clips</MenuItem>
            </TextField>
            {form.scope === 'department' && (
              <TextField
                fullWidth
                label="Department ID"
                type="number"
                value={form.departmentId ?? ''}
                onChange={(e) => setForm((p) => ({ ...p, departmentId: Number(e.target.value) }))}
              />
            )}
            <TextField
              fullWidth
              label="Webhook URL"
              value={form.url ?? ''}
              onChange={(e) => setForm((p) => ({ ...p, url: e.target.value }))}
              placeholder="https://hooks.slack.com/services/..."
            />
            <TextField
              fullWidth
              label="Label (optional)"
              value={form.label ?? ''}
              onChange={(e) => setForm((p) => ({ ...p, label: e.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={!form.url || !form.type || createMutation.isPending}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

// ─── Email Section ────────────────────────────────────────────────────────────

const EmailSection: React.FC = () => {
  const { data: configs = [], isLoading } = useDispatchEmailConfigQuery();
  const createMutation = useCreateDispatchEmailConfigMutation();
  const deleteMutation = useDeleteDispatchEmailConfigMutation();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [address, setAddress] = useState('');
  const [label, setLabel] = useState('');

  const handleCreate = async () => {
    if (!address) return;
    await createMutation.mutateAsync({ address, label: label || undefined });
    setDialogOpen(false);
    setAddress('');
    setLabel('');
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" mb={2} sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6">Email Notifications</Typography>
            <Typography variant="body2" sx={SUBTITLE_SX}>
              Email addresses notified when your clips export. Personal — only your clips trigger these.
            </Typography>
          </Box>
          <Button startIcon={<AddIcon />} variant="contained" size="small" onClick={() => setDialogOpen(true)}>
            Add
          </Button>
        </Stack>

        {isLoading && <Typography color="text.secondary">Loading…</Typography>}

        {!isLoading && configs.length === 0 && (
          <Alert severity="info" sx={{ mt: 1 }}>No email addresses configured.</Alert>
        )}

        <Stack spacing={1} sx={{ mt: 1 }}>
          {configs.map((cfg) => (
            <Stack
              key={cfg.id}
              direction="row"
              sx={{ px: 1.5, py: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, alignItems: 'center', justifyContent: 'space-between' }}
            >
              <Box>
                {cfg.label && <Typography variant="body2" fontWeight={500}>{cfg.label}</Typography>}
                <Typography variant="body2" color="text.secondary">{cfg.address}</Typography>
              </Box>
              <Tooltip title="Remove">
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => deleteMutation.mutate(cfg.id)}
                  disabled={deleteMutation.isPending}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          ))}
        </Stack>
      </CardContent>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Email Address</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
            />
            <TextField
              fullWidth
              label="Label (optional)"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. Coaching Staff"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={!address || createMutation.isPending}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

// ─── S3 Destinations Section ─────────────────────────────────────────────────

const AUTH_MODE_LABELS: Record<S3DestinationAuthMode, string> = {
  static: 'Static AWS keys',
  'assume-role': 'Cross-account role (AssumeRole)',
  'default-chain': 'Our scheduler role (no extra credentials)',
};

const isCreateValid = (form: Partial<CreateS3DestinationData>): form is CreateS3DestinationData => {
  if (!form.bucket || !form.region || !form.authMode) return false;
  if (form.authMode === 'static') return !!form.accessKey && !!form.secretKey;
  if (form.authMode === 'assume-role') return !!form.roleArn;
  return true; // default-chain
};

const S3DestinationsSection: React.FC = () => {
  const { data: destinations = [], isLoading } = useDispatchS3DestinationsQuery();
  const createMutation = useCreateDispatchS3DestinationMutation();
  const deleteMutation = useDeleteDispatchS3DestinationMutation();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<Partial<CreateS3DestinationData>>({ authMode: 'static' });

  const handleCreate = async () => {
    if (!isCreateValid(form)) return;
    await createMutation.mutateAsync(form);
    setDialogOpen(false);
    setForm({ authMode: 'static' });
  };

  const setField = (key: keyof CreateS3DestinationData, value: string) =>
    setForm((p) => ({ ...p, [key]: value }));

  const field = (key: keyof CreateS3DestinationData, label: string, opts?: { type?: string; placeholder?: string }) => (
    <TextField
      fullWidth
      label={label}
      type={opts?.type ?? 'text'}
      placeholder={opts?.placeholder}
      value={(form[key] as string) ?? ''}
      onChange={(e) => setField(key, e.target.value)}
    />
  );

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" mb={2} sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6">S3 Copy Destinations</Typography>
            <Typography variant="body2" sx={SUBTITLE_SX}>
              Automatically copy exported clips to additional S3 buckets. Org-level — applies to all clips.
            </Typography>
          </Box>
          <Button startIcon={<AddIcon />} variant="contained" size="small" onClick={() => setDialogOpen(true)}>
            Add
          </Button>
        </Stack>

        {isLoading && <Typography color="text.secondary">Loading…</Typography>}

        {!isLoading && destinations.length === 0 && (
          <Alert severity="info" sx={{ mt: 1 }}>No S3 destinations configured.</Alert>
        )}

        <Stack spacing={1} sx={{ mt: 1 }}>
          {destinations.map((dest) => (
            <Stack
              key={dest.id}
              direction="row"
              sx={{ px: 1.5, py: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, alignItems: 'center', justifyContent: 'space-between' }}
            >
              <Box>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                  {dest.label && <Typography variant="body2" fontWeight={500}>{dest.label}</Typography>}
                  <Chip
                    label={AUTH_MODE_LABELS[dest.authMode]}
                    size="small"
                    color={dest.authMode === 'assume-role' ? 'primary' : 'default'}
                    variant="outlined"
                  />
                </Stack>
                <Typography variant="body2">
                  {dest.bucket}{dest.baseDir ? `/${dest.baseDir}` : ''}{' '}
                  <Typography component="span" variant="caption" color="text.secondary">({dest.region})</Typography>
                </Typography>
                {dest.authMode === 'static' && (
                  <Typography variant="caption" color="text.secondary">
                    Access key: {dest.accessKey} · Secret: {dest.secretKey}
                  </Typography>
                )}
                {dest.authMode === 'assume-role' && (
                  <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                    Role: {dest.roleArn} · External ID: {dest.externalId}
                  </Typography>
                )}
              </Box>
              <Tooltip title="Remove">
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => deleteMutation.mutate(dest.id)}
                  disabled={deleteMutation.isPending}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          ))}
        </Stack>
      </CardContent>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add S3 Destination</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {field('label', 'Label (optional)', { placeholder: 'e.g. Coaching Staff Bucket' })}
            <Stack direction="row" spacing={2}>
              {field('bucket', 'Bucket name')}
              {field('region', 'Region', { placeholder: 'us-east-1' })}
            </Stack>
            {field('baseDir', 'Base directory (optional)', { placeholder: 'clips/exports' })}

            <TextField
              select
              fullWidth
              label="Authentication mode"
              value={form.authMode ?? 'static'}
              onChange={(e) =>
                setForm((p) => ({
                  ...p,
                  authMode: e.target.value as S3DestinationAuthMode,
                  // Clear other-mode fields on switch to avoid sending mixed payloads.
                  accessKey: undefined,
                  secretKey: undefined,
                  roleArn: undefined,
                  externalId: undefined,
                }))
              }
              helperText="How the scheduler authenticates against the destination bucket"
            >
              <MenuItem value="static">Static AWS keys — you give us an Access Key / Secret</MenuItem>
              <MenuItem value="assume-role">Cross-account role — you grant our scheduler permission to assume a role in your account</MenuItem>
              <MenuItem value="default-chain">Use our scheduler's identity — you grant access via your bucket policy</MenuItem>
            </TextField>

            {form.authMode === 'static' && (
              <>
                {field('accessKey', 'AWS Access Key ID')}
                {field('secretKey', 'AWS Secret Access Key', { type: 'password' })}
              </>
            )}

            {form.authMode === 'assume-role' && (
              <>
                {field('roleArn', 'Role ARN', { placeholder: 'arn:aws:iam::123456789012:role/c360-clip-uploader' })}
                <TextField
                  fullWidth
                  label="External ID (optional — we generate one if empty)"
                  value={form.externalId ?? ''}
                  onChange={(e) => setField('externalId', e.target.value)}
                  helperText="Add this value as an sts:ExternalId condition on your role's trust policy. We'll show the final value once the destination is created."
                />
                <Typography variant="caption" color="text.secondary">
                  Your role must trust <code>arn:aws:iam::616137711661:role/c360-scheduler</code> and grant <code>s3:PutObject</code> on the bucket.
                </Typography>
              </>
            )}

            {form.authMode === 'default-chain' && (
              <Typography variant="caption" color="text.secondary">
                No extra credentials needed. Add a bucket policy on your destination bucket allowing
                {' '}<code>s3:PutObject</code> from <code>arn:aws:iam::616137711661:role/c360-scheduler</code>.
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={!isCreateValid(form) || createMutation.isPending}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const ClipDispatchConfig: React.FC = () => (
  <Box sx={{ mt: 3 }}>
    <Stack spacing={3}>
      <WebhooksSection />
      <Divider />
      <EmailSection />
      <Divider />
      <S3DestinationsSection />
    </Stack>
  </Box>
);

export default ClipDispatchConfig;
